import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  Clock,
  Users,
  Receipt,
  Tag,
  Layers,
  AlertTriangle,
  ExternalLink,
  Mail,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Package, FeatureDefinition } from '../../types/firestoreTypes';

// Type for localized fields
type LocalizedFields = {
  [key: `name_${string}`]: string;
  [key: `description_${string}`]: string;
};

// Ordered list of key features to display
const KEY_FEATURE_IDS_TO_DISPLAY = [
  'PRICE_TIER_LIMIT',
  'BILLING_PROFILE_LIMIT',
  'BILLING_ACCESS_ENABLED',
  'INVOICE_SINGLE_SESSION',
  'INVOICE_BULK_SESSION',
  'EMAIL_NOTIFICATIONS',
];

interface SubscriptionTabProps {
  // No props needed as we're using context and fetching data
}

const SubscriptionTab: React.FC<SubscriptionTabProps> = () => {
  const { therapist } = useAuth();
  const { t, i18n } = useTranslation();
  const [activePackageDetails, setActivePackageDetails] = useState<Package | null>(null);
  const [isPackageLoading, setIsPackageLoading] = useState(true);
  const [featureDetailsMap, setFeatureDetailsMap] = useState<Map<string, FeatureDefinition>>(new Map());
  const [areFeaturesLoading, setAreFeaturesLoading] = useState(true);

  // Fetch package details with localization
  useEffect(() => {
    const fetchPackageDetails = async () => {
      if (therapist?.currentPackageId) {
        setIsPackageLoading(true);
        try {
          const packageDocRef = doc(db, 'packages', therapist.currentPackageId);
          const packageDocSnap = await getDoc(packageDocRef);
          if (packageDocSnap.exists()) {
            const pkgData = packageDocSnap.data() as Package & LocalizedFields;
            const lang = i18n.language.split('-')[0]; // e.g., 'en', 'hu'

            // Construct localized package details
            const localizedPackageData: Package = {
              ...pkgData, // Spread other package data like price, features, etc.
              id: packageDocSnap.id, // Ensure ID is set
              name: pkgData[`name_${lang}` as keyof LocalizedFields] || pkgData.name_en || pkgData.name,
              description: pkgData[`description_${lang}` as keyof LocalizedFields] || pkgData.description_en || pkgData.description,
            };
            setActivePackageDetails(localizedPackageData);
          } else {
            console.error('Package not found:', therapist.currentPackageId);
            setActivePackageDetails(null);
          }
        } catch (error) {
          console.error('Error fetching package details:', error);
          setActivePackageDetails(null);
        } finally {
          setIsPackageLoading(false);
        }
      } else {
        setIsPackageLoading(false);
        setActivePackageDetails(null);
      }
    };

    fetchPackageDetails();
  }, [therapist?.currentPackageId, i18n.language]);

  // Fetch all feature definitions
  useEffect(() => {
    const fetchAllFeatureDefinitions = async () => {
      setAreFeaturesLoading(true);
      try {
        const featuresCollectionRef = collection(db, 'features');
        const featuresSnapshot = await getDocs(featuresCollectionRef);
        const newFeaturesMap = new Map<string, FeatureDefinition>();
        featuresSnapshot.forEach((docSnap) => {
          newFeaturesMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() } as FeatureDefinition);
        });
        setFeatureDetailsMap(newFeaturesMap);
      } catch (error) {
        console.error('Error fetching feature definitions:', error);
      } finally {
        setAreFeaturesLoading(false);
      }
    };
    fetchAllFeatureDefinitions();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Active':
        return <CheckCircle className="w-6 h-6 text-emerald-500" />;
      case 'Trial':
        return <Clock className="w-6 h-6 text-amber-500" />;
      default:
        return <AlertTriangle className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Trial':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const currentStatus = therapist?.status || 'Unknown';
  const packageName = activePackageDetails?.name || therapist?.currentPackageId || 'N/A';
  const packageDescription = activePackageDetails?.description || 'Details not available.';
  const expiryDateForDisplay = therapist?.packageExpiryDate ? therapist.packageExpiryDate.toDate() : undefined;

  if (isPackageLoading || areFeaturesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7F56D9] border-t-transparent"></div>
        <span className="ml-3 text-gray-600">{t('subscriptionTab.loading.subscriptionDetails')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">{t('subscriptionTab.mainTitle')}</h1>
      </div>

      {/* Account Status Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {getStatusIcon(currentStatus)}
            <div>
              <h2 className="text-lg font-medium text-gray-900">{t('subscriptionTab.accountStatusTitle')}</h2>
              <p className={`mt-1 px-3 py-1 rounded-full text-sm font-medium inline-flex ${getStatusColor(currentStatus)}`}>
                {currentStatus}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Current Plan Details Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-gray-900">{packageName}</h2>
          <p className="text-gray-600">{packageDescription}</p>
          
          {currentStatus === 'Trial' && expiryDateForDisplay && (
            <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <span className="font-medium text-amber-700">
                  Trial Expires On: {format(expiryDateForDisplay, "MMMM d, yyyy 'at' h:mm a")}
                </span>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {currentStatus === 'Trial' && (
              <Button variant="primary" className="flex items-center">
                Upgrade Now
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Features & Limits Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-6">{t('subscriptionTab.featuresAndLimitsTitle')}</h2>
        {areFeaturesLoading ? (
          <p>{t('subscriptionTab.loading.features')}</p>
        ) : (
          <div className="grid gap-6">
            {therapist?.packageFeaturesSnapshot && KEY_FEATURE_IDS_TO_DISPLAY.map((featureId) => {
              if (therapist.packageFeaturesSnapshot && therapist.packageFeaturesSnapshot.hasOwnProperty(featureId)) {
                const value = therapist.packageFeaturesSnapshot[featureId];
                const featureDetail = featureDetailsMap.get(featureId) as (FeatureDefinition & LocalizedFields) | undefined;
                const lang = i18n.language.split('-')[0];

                const displayName = featureDetail?.[`name_${lang}` as keyof LocalizedFields] || featureDetail?.name_en || featureDetail?.name || featureId;
                const descriptionText = featureDetail?.[`description_${lang}` as keyof LocalizedFields] || featureDetail?.description_en || featureDetail?.description || t('subscriptionTab.features.defaultDescription');
                
                const displayValue = typeof value === 'boolean'
                  ? (value ? t('subscriptionTab.features.included') : t('subscriptionTab.features.notIncluded'))
                  : typeof value === 'number'
                    ? `${value}`
                    : String(value);

                // Determine the icon based on featureId
                let icon = null;
                if (featureId.includes('PATIENT')) icon = <Users className="w-5 h-5 text-indigo-600" />;
                else if (featureId === 'PRICE_TIER_LIMIT') icon = <Tag className="w-5 h-5 text-indigo-600" />;
                else if (featureId === 'BILLING_PROFILE_LIMIT') icon = <Receipt className="w-5 h-5 text-indigo-600" />;
                else if (featureId === 'BILLING_ACCESS_ENABLED') icon = <Receipt className="w-5 h-5 text-indigo-600" />;
                else if (featureId === 'INVOICE_SINGLE_SESSION') icon = <Receipt className="w-5 h-5 text-indigo-600" />;
                else if (featureId === 'INVOICE_BULK_SESSION') icon = <Layers className="w-5 h-5 text-indigo-600" />;
                else if (featureId === 'EMAIL_NOTIFICATIONS') icon = <Mail className="w-5 h-5 text-indigo-600" />;

                return (
                  <div key={featureId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {icon && (
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          {icon}
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium text-gray-900">{displayName}</h3>
                        <p className="text-sm text-gray-500">
                          {descriptionText}
                        </p>
                      </div>
                    </div>
                    <span className="font-medium text-gray-900">{displayValue}</span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>

      {/* Contact Support Section */}
      <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center space-x-2 text-gray-600">
          <Mail className="w-5 h-5" />
          <span>{t('subscriptionTab.contact.prompt')} </span>
          <a href="mailto:hello@praxi.hu" className="text-indigo-600 hover:text-indigo-500">
            hello@praxi.hu
          </a>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTab;